import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import client from '../../api/client';
import Navbar from '../../components/Navbar';
import { useAuth } from '../../context/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBanner, setShowBanner] = useState(!!location.state?.showBanner);
  const [deletingId, setDeletingId] = useState(null);

  const trialBlocked = user?.free_trial_used && !user?.is_whitelisted;

  useEffect(() => {
    if (location.state?.showBanner) {
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    client.get('/api/projects/')
      .then(res => setProjects(res.data))
      .catch(() => setError('Failed to load projects.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleDelete(p) {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return;
    setDeletingId(p.id);
    try {
      await client.delete(`/api/projects/${p.id}/`);
      setProjects(prev => prev.filter(x => x.id !== p.id));
    } catch {
      alert('Failed to delete project.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.content}>
        {showBanner && (
          <div style={styles.banner}>
            Project submitted! Your work will be reviewed within one week.
            <button style={styles.bannerClose} onClick={() => setShowBanner(false)}>×</button>
          </div>
        )}

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Client Dashboard</h1>
            <p style={styles.welcome}>Welcome back, <strong>{user?.name}</strong></p>
          </div>
          <div style={styles.newBtnWrap}>
            <button
              style={{ ...styles.newBtn, ...(trialBlocked ? styles.newBtnDisabled : {}) }}
              onClick={() => !trialBlocked && navigate('/projects/new')}
              disabled={trialBlocked}
              title={trialBlocked ? 'Free trial used — contact us to continue' : undefined}
            >
              + New Project
            </button>
            {trialBlocked && (
              <span style={styles.trialHint}>Free trial used — contact us to continue</span>
            )}
          </div>
        </div>

        {loading && <p style={styles.hint}>Loading projects…</p>}
        {error && <p style={styles.error}>{error}</p>}

        {!loading && !error && projects.length === 0 && (
          <div style={styles.empty}>
            <p>No projects yet.</p>
            {!trialBlocked && (
              <button style={styles.newBtn} onClick={() => navigate('/projects/new')}>
                Create your first project
              </button>
            )}
          </div>
        )}

        {projects.length > 0 && (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Project Name', 'Images', 'Labeled', 'Status', 'Created', ''].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id} style={styles.row}>
                    <td style={styles.td}>
                      <Link to={`/projects/${p.id}`} style={styles.link}>{p.name}</Link>
                    </td>
                    <td style={styles.tdCenter}>{p.image_count}</td>
                    <td style={styles.tdCenter}>{p.labeled_count}</td>
                    <td style={styles.tdCenter}>
                      {p.is_done ? (
                        <span style={{ ...styles.badge, ...styles.badgeDone }}>Done</span>
                      ) : (
                        <span style={{ ...styles.badge, ...statusColor(p.status) }}>
                          {p.status}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <button
                        style={styles.deleteBtn}
                        disabled={deletingId === p.id}
                        onClick={() => handleDelete(p)}
                      >
                        {deletingId === p.id ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function statusColor(s) {
  if (s === 'active')   return { background: '#d4edda', color: '#155724' };
  if (s === 'complete') return { background: '#cce5ff', color: '#004085' };
  return { background: '#fff3cd', color: '#856404' };
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'sans-serif' },
  content: { padding: '2rem', maxWidth: '960px', margin: '0 auto' },
  banner: {
    background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb',
    borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '0.9rem',
  },
  bannerClose: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '1.1rem', color: '#155724', lineHeight: 1,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title: { margin: 0, fontSize: '1.6rem', color: '#2c3e50' },
  welcome: { margin: '0.25rem 0 0', color: '#555' },
  newBtnWrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' },
  newBtn: {
    background: '#2c3e50', color: '#fff', border: 'none',
    borderRadius: '6px', padding: '0.6rem 1.2rem',
    fontSize: '0.9rem', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  newBtnDisabled: { background: '#aaa', cursor: 'not-allowed' },
  trialHint: { fontSize: '0.78rem', color: '#856404' },
  hint: { color: '#888' },
  error: { color: '#c0392b' },
  empty: { textAlign: 'center', marginTop: '4rem', color: '#666' },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  th: {
    textAlign: 'left', padding: '0.75rem 1rem',
    background: '#2c3e50', color: '#fff', fontSize: '0.85rem',
  },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '0.75rem 1rem', fontSize: '0.9rem', color: '#333' },
  tdCenter: { padding: '0.75rem 1rem', fontSize: '0.9rem', color: '#333', textAlign: 'center' },
  badge: {
    display: 'inline-block', padding: '0.2rem 0.6rem',
    borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600,
    textTransform: 'capitalize',
  },
  badgeDone: { background: '#cce5ff', color: '#004085' },
  link: { color: '#2c3e50', fontWeight: 600, textDecoration: 'none' },
  deleteBtn: {
    background: 'none', border: '1px solid #c0392b', color: '#c0392b',
    borderRadius: '4px', padding: '0.25rem 0.6rem', fontSize: '0.8rem',
    cursor: 'pointer',
  },
};
